// frontend/src/pages/inventaire/clients.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Divider,
  Tab,
  Tabs,
  Alert,
  Snackbar,
  CircularProgress,
  Menu,
  MenuItem,
  Tooltip,
  FormControlLabel,
  Switch,
  Avatar,
  Badge
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  MonetizationOn as CreditIcon,
  Receipt as ReceiptIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  CreditCard as PaymentIcon,
  People as PeopleIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  AttachMoney as MoneyIcon,
  Loyalty as LoyaltyIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Star as StarIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser } from '../../services/auth';
import api from '../../services/auth';
import { formatCurrency, formatDate, formatPhoneNumber } from '../../../src/components/utils/formatters';
import ClientDetails from '../../scenes/Clients/ClientDetails';
import PaymentCreditModal from '../../scenes/Clients/PaymentCreditModal';
import VentesClientModal from '../../scenes/Clients/VentesClientModal';
import HistoriqueFideliteModal from '../../scenes/Clients/HistoriqueFideliteModal';
import HistoriqueCreditModal from '../../scenes/Clients/HistoriqueCreditModal';

const ClientsPage = () => {
  // const { user } = useAuth();
  const navigate = useNavigate();
  
  // États principaux
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalClients, setTotalClients] = useState(0);
  
  // Recherche et filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterActif, setFilterActif] = useState('tous');
  const [anchorElFilter, setAnchorElFilter] = useState(null);
  
  // États pour les modales
  const [openDialog, setOpenDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [viewingClient, setViewingClient] = useState(null);
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [openVentesModal, setOpenVentesModal] = useState(false);
  const [openHistoriqueFidelite, setOpenHistoriqueFidelite] = useState(false);
  const [openHistoriqueCredit, setOpenHistoriqueCredit] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  
  // Formulaire client
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    telephone: '',
    email: '',
    adresse: '',
    notes: '',
    limite_credit: 0,
    est_actif: true
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  
  // Onglets pour les détails client
  const [tabValue, setTabValue] = useState(0);
  
  // Stats clients avec crédit
  const [clientsAvecCredit, setClientsAvecCredit] = useState([]);
  const [loadingCredit, setLoadingCredit] = useState(false);

  // Effet pour debounce de recherche
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Chargement des clients
  useEffect(() => {
    fetchClients();
    fetchClientsAvecCredit();
  }, [page, rowsPerPage, debouncedSearchTerm, filterActif]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        page_size: rowsPerPage,
        search: debouncedSearchTerm || undefined,
        ordering: 'nom'
      };
      
      if (filterActif !== 'tous') {
        params.est_actif = filterActif === 'actif';
      }
      
      const response = await api.get('inventory/clients/', { params });
      setClients(response.data.results || response.data);
      setTotalClients(response.data.count || response.data.length);
    } catch (err) {
      console.error('Erreur chargement clients:', err);
      setError('Impossible de charger la liste des clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchClientsAvecCredit = async () => {
    try {
      setLoadingCredit(true);
      const response = await api.get('inventory/clients/avec_credit/');
      setClientsAvecCredit(response.data);
    } catch (err) {
      console.error('Erreur chargement clients avec crédit:', err);
    } finally {
      setLoadingCredit(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (client = null) => {
    if (client) {
      setEditingClient(client);
      setFormData({
        nom: client.nom || '',
        prenom: client.prenom || '',
        telephone: client.telephone || '',
        email: client.email || '',
        adresse: client.adresse || '',
        notes: client.notes || '',
        limite_credit: client.limite_credit || 0,
        est_actif: client.est_actif !== undefined ? client.est_actif : true
      });
    } else {
      setEditingClient(null);
      setFormData({
        nom: '',
        prenom: '',
        telephone: '',
        email: '',
        adresse: '',
        notes: '',
        limite_credit: 0,
        est_actif: true
      });
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingClient(null);
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.nom.trim()) {
      errors.nom = 'Le nom est requis';
    }
    if (!formData.telephone.trim()) {
      errors.telephone = 'Le téléphone est requis';
    }
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email invalide';
    }
    if (formData.limite_credit < 0) {
      errors.limite_credit = 'La limite de crédit doit être positive';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setSubmitting(true);
    try {
      if (editingClient) {
        await api.patch(`inventory/clients/${editingClient.id}/`, formData);
        setSuccess('Client modifié avec succès');
      } else {
        await api.post('inventory/clients/', formData);
        setSuccess('Client créé avec succès');
      }
      handleCloseDialog();
      fetchClients();
      fetchClientsAvecCredit();
    } catch (err) {
      console.error('Erreur sauvegarde client:', err);
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (client) => {
    setClientToDelete(client);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await api.delete(`inventory/clients/${clientToDelete.id}/`);
      setSuccess('Client supprimé avec succès');
      setDeleteDialogOpen(false);
      setClientToDelete(null);
      fetchClients();
      fetchClientsAvecCredit();
    } catch (err) {
      console.error('Erreur suppression client:', err);
      setError('Impossible de supprimer ce client');
    }
  };

  const handleViewDetails = (client) => {
    setViewingClient(client);
    setSelectedClient(client);
    setTabValue(0);
  };

  const handleCloseDetails = () => {
    setViewingClient(null);
    setSelectedClient(null);
  };

  const handlePaymentCredit = (client) => {
    setSelectedClient(client);
    setOpenPaymentModal(true);
  };

  const handleViewVentes = (client) => {
    setSelectedClient(client);
    setOpenVentesModal(true);
  };

  const handleViewHistoriqueFidelite = (client) => {
    setSelectedClient(client);
    setOpenHistoriqueFidelite(true);
  };

  const handleViewHistoriqueCredit = (client) => {
    setSelectedClient(client);
    setOpenHistoriqueCredit(true);
  };

  const handlePaymentSuccess = () => {
    fetchClients();
    fetchClientsAvecCredit();
    setSuccess('Paiement enregistré avec succès');
  };

  // Gestionnaires de filtres
  const handleFilterClick = (event) => {
    setAnchorElFilter(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorElFilter(null);
  };

  const handleFilterChange = (value) => {
    setFilterActif(value);
    setPage(0);
    handleFilterClose();
  };

  // Menu pour chaque ligne
  const ClientActionsMenu = ({ client }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    
    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
    };
    
    const handleClose = () => {
      setAnchorEl(null);
    };
    
    return (
      <>
        <IconButton onClick={handleClick} size="small">
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
        >
          <MenuItem onClick={() => { handleViewDetails(client); handleClose(); }}>
            <VisibilityIcon fontSize="small" sx={{ mr: 1 }} /> Détails
          </MenuItem>
          <MenuItem onClick={() => { handleOpenDialog(client); handleClose(); }}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} /> Modifier
          </MenuItem>
          <MenuItem onClick={() => { handleViewVentes(client); handleClose(); }}>
            <ReceiptIcon fontSize="small" sx={{ mr: 1 }} /> Ventes
          </MenuItem>
          {client.solde_credit > 0 && (
            <MenuItem onClick={() => { handlePaymentCredit(client); handleClose(); }}>
              <PaymentIcon fontSize="small" sx={{ mr: 1 }} /> Paiement crédit
            </MenuItem>
          )}
          <MenuItem onClick={() => { handleViewHistoriqueCredit(client); handleClose(); }}>
            <CreditIcon fontSize="small" sx={{ mr: 1 }} /> Historique crédit
          </MenuItem>
          <MenuItem onClick={() => { handleViewHistoriqueFidelite(client); handleClose(); }}>
            <LoyaltyIcon fontSize="small" sx={{ mr: 1 }} /> Points fidélité
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => { handleDeleteClick(client); handleClose(); }} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Supprimer
          </MenuItem>
        </Menu>
      </>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Clients
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nouveau client
        </Button>
      </Box>

      {/* Cartes statistiques */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total clients
                  </Typography>
                  <Typography variant="h4">
                    {totalClients}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                  <PeopleIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Avec crédit
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {clientsAvecCredit.length}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.main', width: 56, height: 56 }}>
                  <CreditIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total crédit dû
                  </Typography>
                  <Typography variant="h4" color="error.main">
                    {(clientsAvecCredit.reduce((sum, c) => sum + parseFloat(c.solde_credit || 0), 0))}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'error.main', width: 56, height: 56 }}>
                  <MoneyIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        {/* <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent> */}
              {/* <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Points fidélité
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {clients.reduce((sum, c) => sum + (c.points_fidelite || 0), 0)}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}>
                  <StarIcon />
                </Avatar>
              </Box> */}
            {/* </CardContent>
          </Card>
        </Grid> */}
      </Grid>

      {/* Barre de recherche et filtres */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Rechercher par nom, prénom, téléphone ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setSearchTerm('')} size="small">
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={handleFilterClick}
            >
              {filterActif === 'tous' ? 'Tous' : filterActif === 'actif' ? 'Actifs' : 'Inactifs'}
            </Button>
            <Menu
              anchorEl={anchorElFilter}
              open={Boolean(anchorElFilter)}
              onClose={handleFilterClose}
            >
              <MenuItem onClick={() => handleFilterChange('tous')}>Tous</MenuItem>
              <MenuItem onClick={() => handleFilterChange('actif')}>Actifs</MenuItem>
              <MenuItem onClick={() => handleFilterChange('inactif')}>Inactifs</MenuItem>
            </Menu>
          </Grid>
        </Grid>
      </Paper>

      {/* Tableau des clients */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Client</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Crédit</TableCell>
                <TableCell align="center">Points fidélité</TableCell>
                <TableCell align="right">Total achats</TableCell>
                <TableCell align="center">Statut</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : clients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="textSecondary">
                      Aucun client trouvé
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                clients.map((client) => (
                  <TableRow key={client.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2, bgcolor: 'primary.light' }}>
                          {client.nom.charAt(0)}{client.prenom?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2">
                            {client.nom_complet || `${client.nom} ${client.prenom || ''}`}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            ID: {client.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        {client.telephone && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <PhoneIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                            <Typography variant="body2">
                              {(client.telephone)}
                            </Typography>
                          </Box>
                        )}
                        {client.email && (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <EmailIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {client.email}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {client.limite_credit > 0 ? (
                        <Box>
                          <Typography variant="body2">
                            <strong>Limite:</strong> {(client.limite_credit)}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            color={client.solde_credit > 0 ? 'error.main' : 'success.main'}
                          >
                            <strong>Solde:</strong> {(client.solde_credit)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Disponible: {(client.credit_disponible)}
                          </Typography>
                        </Box>
                      ) : (
                        <Chip label="Pas de crédit" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={<StarIcon />}
                        label={client.points_fidelite || 0}
                        size="small"
                        color={client.points_fidelite > 0 ? "warning" : "default"}
                        variant={client.points_fidelite > 0 ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {(client.total_achats || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={client.est_actif ? <CheckCircleIcon /> : <CancelIcon />}
                        label={client.est_actif ? 'Actif' : 'Inactif'}
                        size="small"
                        color={client.est_actif ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <ClientActionsMenu client={client} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalClients}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Lignes par page"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
        />
      </Paper>

      {/* Modal d'ajout/modification client */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingClient ? 'Modifier le client' : 'Nouveau client'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Nom"
                name="nom"
                value={formData.nom}
                onChange={handleInputChange}
                error={!!formErrors.nom}
                helperText={formErrors.nom}
                disabled={submitting}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Prénom"
                name="prenom"
                value={formData.prenom}
                onChange={handleInputChange}
                disabled={submitting}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Téléphone"
                name="telephone"
                value={formData.telephone}
                onChange={handleInputChange}
                error={!!formErrors.telephone}
                helperText={formErrors.telephone}
                disabled={submitting}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                error={!!formErrors.email}
                helperText={formErrors.email}
                disabled={submitting}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Adresse"
                name="adresse"
                multiline
                rows={2}
                value={formData.adresse}
                onChange={handleInputChange}
                disabled={submitting}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Limite de crédit (HTG)"
                name="limite_credit"
                type="number"
                value={formData.limite_credit}
                onChange={handleInputChange}
                error={!!formErrors.limite_credit}
                helperText={formErrors.limite_credit}
                disabled={submitting}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">HTG</InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    name="est_actif"
                    checked={formData.est_actif}
                    onChange={handleInputChange}
                    disabled={submitting || !editingClient}
                  />
                }
                label="Client actif"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={handleInputChange}
                disabled={submitting}
                placeholder="Informations supplémentaires..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : null}
          >
            {submitting ? 'Enregistrement...' : editingClient ? 'Modifier' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmation suppression */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer le client{' '}
            <strong>
              {clientToDelete?.nom_complet || `${clientToDelete?.nom} ${clientToDelete?.prenom || ''}`}
            </strong>
            ?
          </Typography>
          {clientToDelete?.solde_credit > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Ce client a un solde créditeur de {(clientToDelete.solde_credit)}.
              La suppression est déconseillée.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Annuler</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de détails client */}
      {viewingClient && (
        <Dialog open={!!viewingClient} onClose={handleCloseDetails} maxWidth="lg" fullWidth>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                {viewingClient.nom.charAt(0)}{viewingClient.prenom?.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="h6">
                  {viewingClient.nom_complet || `${viewingClient.nom} ${viewingClient.prenom || ''}`}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Client depuis le {(viewingClient.date_creation)}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleCloseDetails} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 3 }}>
              <Tab label="Informations" />
              <Tab label="Ventes" />
              <Tab label="Crédit" />
              <Tab label="Fidélité" />
            </Tabs>
            
            {tabValue === 0 && (
              <ClientDetails 
                client={viewingClient} 
                onEdit={() => {
                  handleCloseDetails();
                  handleOpenDialog(viewingClient);
                }}
                onPayment={() => {
                  handleCloseDetails();
                  handlePaymentCredit(viewingClient);
                }}
              />
            )}
            
            {tabValue === 1 && (
              <VentesClientModal 
                client={viewingClient}
                embedded={true}
              />
            )}
            
            {tabValue === 2 && (
              <HistoriqueCreditModal 
                client={viewingClient}
                embedded={true}
                onPayment={() => {
                  setOpenHistoriqueCredit(false);
                  handlePaymentCredit(viewingClient);
                }}
              />
            )}
            
            {tabValue === 3 && (
              <HistoriqueFideliteModal 
                client={viewingClient}
                embedded={true}
              />
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Modal de paiement crédit */}
      {selectedClient && (
        <PaymentCreditModal
          open={openPaymentModal}
          onClose={() => setOpenPaymentModal(false)}
          client={selectedClient}
          onSuccess={handlePaymentSuccess}
        />
      )}

      {/* Modal des ventes client */}
      {selectedClient && (
        <VentesClientModal
          open={openVentesModal}
          onClose={() => setOpenVentesModal(false)}
          client={selectedClient}
        />
      )}

      {/* Modal historique fidélité */}
      {selectedClient && (
        <HistoriqueFideliteModal
          open={openHistoriqueFidelite}
          onClose={() => setOpenHistoriqueFidelite(false)}
          client={selectedClient}
        />
      )}

      {/* Modal historique crédit */}
      {selectedClient && (
        <HistoriqueCreditModal
          open={openHistoriqueCredit}
          onClose={() => setOpenHistoriqueCredit(false)}
          client={selectedClient}
          onPayment={() => {
            setOpenHistoriqueCredit(false);
            handlePaymentCredit(selectedClient);
          }}
        />
      )}

      {/* Snackbar notifications */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" variant="filled">
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setError(null)} severity="error" variant="filled">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientsPage;